import { useEffect } from 'react';

// Locks page scroll while `isLocked` is true; restores the exact prior state on unlock/unmount.
//
// Both web and admin shells put the real scroll container on a nested `<main class="overflow-y-auto">`
// (not document.body/html — the root layout is `h-screen overflow-hidden`), so toggling
// body/html overflow alone does nothing: wheel/touch events still scroll-chain into that
// container. This locks body/html overflow (for apps that do scroll there, and to suppress
// any scrollbar) AND intercepts wheel/touch events globally so background scrolling is blocked
// regardless of which element actually owns the scroll — while still allowing scroll gestures
// that originate inside an open <dialog> (e.g. a tall modal-box).
export function useScrollLock(isLocked: boolean) {
  useEffect(() => {
    if (!isLocked) return;

    const { body, documentElement: html } = document;
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    const prevBodyOverflow = body.style.overflow;
    const prevBodyPaddingRight = body.style.paddingRight;
    const prevHtmlOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    // Compensate for the removed scrollbar so the page doesn't shift/reflow.
    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    const blockBackgroundScroll = (e: Event) => {
      if ((e.target as Element | null)?.closest('dialog')) return;
      e.preventDefault();
    };
    document.addEventListener('wheel', blockBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', blockBackgroundScroll, { passive: false });

    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.paddingRight = prevBodyPaddingRight;
      html.style.overflow = prevHtmlOverflow;
      document.removeEventListener('wheel', blockBackgroundScroll);
      document.removeEventListener('touchmove', blockBackgroundScroll);
    };
  }, [isLocked]);
}
